import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { RichTextNodeType } from '@dossierhq/core';
import { ButtonDropdown, Dialog2, Icon, IconButton, Row, toSpacingClassName, } from '@dossierhq/design';
import { $createCodeNode, $isCodeNode } from '@lexical/code';
import { CODE_LANGUAGE_FRIENDLY_NAME_MAP, CODE_LANGUAGE_MAP, getLanguageFriendlyName, } from '@lexical/code-prism';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode, } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext.js';
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { $createParagraphNode, $getNodeByKey, $getSelection, $isRangeSelection, $isRootOrShadowRoot, $onUpdate, COMMAND_PRIORITY_CRITICAL, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND, } from 'lexical';
import { useCallback, useContext, useEffect, useState } from 'react';
import { DossierContext } from '../../contexts/DossierContext.js';
import { getSelectedNode } from '../../third-party/lexical-playground/utils/getSelectedNode.js';
import { assertExhaustive } from '../../utils/AssertUtils.js';
import { AdminEntitySelectorDialog } from '../AdminEntitySelectorDialog/AdminEntitySelectorDialog.js';
import { AdminTypePickerDialog } from '../AdminTypePickerDialog/AdminTypePickerDialog.js';
import { INSERT_ADMIN_COMPONENT_COMMAND } from './AdminComponentNode.js';
import { $isAdminEntityLinkNode, TOGGLE_ADMIN_ENTITY_LINK_COMMAND } from './AdminEntityLinkNode.js';
import { INSERT_ADMIN_ENTITY_COMMAND } from './AdminEntityNode.js';
import { CreateLinkDialog } from './CreateLinkDialog.js';
const blockTypeToBlockName = {
    paragraph: { title: 'Paragraph', node: RichTextNodeType.paragraph, icon: 'paragraph' },
    bullet: { title: 'Bulleted list', node: RichTextNodeType.list, icon: 'listUl' },
    number: { title: 'Numbered list', node: RichTextNodeType.list, icon: 'listOl' },
    check: { title: 'Check list', node: RichTextNodeType.list, icon: 'listCheck' },
    h1: { title: 'Heading 1', node: RichTextNodeType.heading, icon: 'heading' },
    h2: { title: 'Heading 2', node: RichTextNodeType.heading, icon: 'heading' },
    h3: { title: 'Heading 3', node: RichTextNodeType.heading, icon: 'heading' },
    h4: { title: 'Heading 4', node: RichTextNodeType.heading, icon: 'heading' },
    h5: { title: 'Heading 5', node: RichTextNodeType.heading, icon: 'heading' },
    h6: { title: 'Heading 6', node: RichTextNodeType.heading, icon: 'heading' },
    code: { title: 'Code block', node: RichTextNodeType.code, icon: 'code' },
};
export function ToolbarPlugin({ fieldSpec }) {
    const { schema } = useContext(DossierContext);
    const [editor] = useLexicalComposerContext();
    const [blockType, setBlockType] = useState('paragraph');
    const [selectedElementKey, setSelectedElementKey] = useState(null);
    const [isLink, setIsLink] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isSubscript, setIsSubscript] = useState(false);
    const [isSuperscript, setIsSuperscript] = useState(false);
    const [isCode, setIsCode] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isEntityLink, setIsEntityLink] = useState(false);
    const [codeLanguage, setCodeLanguage] = useState('');
    const [showAddEntityDialog, setShowAddEntityDialog] = useState(false);
    const [showAddComponentDialog, setShowAddComponentDialog] = useState(false);
    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            //TODO why can't we only use nodes, why looking at the dom?
            const anchorNode = selection.anchor.getNode();
            let element = anchorNode.getKey() === 'root'
                ? anchorNode
                : $findMatchingParent(anchorNode, (e) => {
                    const parent = e.getParent();
                    return parent !== null && $isRootOrShadowRoot(parent);
                });
            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow();
            }
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);
            // Text formatting
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsSubscript(selection.hasFormat('subscript'));
            setIsSuperscript(selection.hasFormat('superscript'));
            setIsCode(selection.hasFormat('code'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            setIsLink($isLinkNode(parent) || $isLinkNode(node));
            setIsEntityLink($isAdminEntityLinkNode(parent) || $isAdminEntityLinkNode(node));
            if (elementDOM !== null) {
                setSelectedElementKey(elementKey);
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                    const type = parentList ? parentList.getListType() : element.getListType();
                    setBlockType(type);
                }
                else {
                    const type = $isHeadingNode(element) ? element.getTag() : element.getType();
                    if (type in blockTypeToBlockName) {
                        setBlockType(type);
                    }
                    if ($isCodeNode(element)) {
                        const language = element.getLanguage();
                        setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : '');
                        return; //TODO why return here?
                    }
                }
            }
        }
    }, [editor]);
    useEffect(() => {
        return mergeRegister(editor.registerCommand(SELECTION_CHANGE_COMMAND, (_payload, newEditor) => {
            // SELECTION_CHANGE_COMMAND runs before reconciliation (Lexical 0.52.0), so defer the
            // DOM-dependent toolbar update until the pending update has committed
            $onUpdate(() => newEditor.read('latest', $updateToolbar));
            return false;
        }, COMMAND_PRIORITY_CRITICAL), editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                $updateToolbar();
            });
        }));
    }, [editor, $updateToolbar]);
    const enableAllNodes = !fieldSpec.richTextNodes || fieldSpec.richTextNodes.length === 0;
    const enableEntityNode = enableAllNodes || fieldSpec.richTextNodes?.includes(RichTextNodeType.entity);
    const enableLinkNode = enableAllNodes || fieldSpec.richTextNodes?.includes(RichTextNodeType.link);
    const enableEntityLinkNode = enableAllNodes || fieldSpec.richTextNodes?.includes(RichTextNodeType.entityLink);
    const enableComponentNode = (enableAllNodes || fieldSpec.richTextNodes?.includes(RichTextNodeType.component)) &&
        schema &&
        schema.getComponentTypeCount() > 0;
    const insertItems = [];
    if (enableEntityNode) {
        insertItems.push({ id: 'entity', name: 'Entity', show: setShowAddEntityDialog });
    }
    if (enableComponentNode) {
        insertItems.push({ id: 'component', name: 'Component', show: setShowAddComponentDialog });
    }
    return (_jsxs(Row, { gap: 2, marginBottom: 2, children: [_jsx(BlockFormatDropDown, { disabled: !editor.isEditable(), blockType: blockType, editor: editor, fieldSpec: fieldSpec }), blockType === 'code' ? (_jsx(CodeLanguageDropdown, { disabled: !editor.isEditable(), editor: editor, codeLanguage: codeLanguage, selectedElementKey: selectedElementKey })) : (_jsxs(_Fragment, { children: [_jsxs(IconButton.Group, { condensed: true, skipBottomMargin: true, children: [_jsx(IconButton, { icon: "bold", toggled: isBold, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold') }), _jsx(IconButton, { icon: "italic", toggled: isItalic, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic') }), _jsx(IconButton, { icon: "subscript", toggled: isSubscript, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript') }), _jsx(IconButton, { icon: "superscript", toggled: isSuperscript, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript') }), _jsx(IconButton, { icon: "code", toggled: isCode, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code') }), _jsx(IconButton, { icon: "underline", toggled: isUnderline, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline') }), _jsx(IconButton, { icon: "strikethrough", toggled: isStrikethrough, onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough') })] }), enableEntityLinkNode || enableLinkNode ? (_jsx(LinkAndEntityLinkButton, { fieldSpec: fieldSpec, enableEntityLinkNode: enableEntityLinkNode, enableLinkNode: enableLinkNode, isEntityLink: isEntityLink, isLink: isLink })) : null, insertItems.length > 0 ? (_jsxs(_Fragment, { children: [_jsx(Row.Item, { flexGrow: 1 }), _jsx(ButtonDropdown, { iconLeft: "add", left: true, items: insertItems, renderItem: (item) => item.name, onItemClick: (item) => item.show(true), children: "Insert" })] })) : null] })), showAddEntityDialog ? (_jsx(AddEntityDialog, { fieldSpec: fieldSpec, onClose: () => setShowAddEntityDialog(false) })) : null, showAddComponentDialog ? (_jsx(AddComponentButton, { fieldSpec: fieldSpec, onClose: () => setShowAddComponentDialog(false) })) : null] }));
}
function BlockFormatDropDown({ editor, blockType, disabled = false, fieldSpec, }) {
    const items = Object.entries(blockTypeToBlockName)
        .filter(([_blockType, blockConfig]) => {
        if (!fieldSpec.richTextNodes || fieldSpec.richTextNodes.length === 0)
            return true;
        return fieldSpec.richTextNodes.includes(blockConfig.node);
    })
        .map(([blockType, blockConfig]) => ({
        id: blockType,
        name: blockConfig.title,
        icon: blockConfig.icon,
    }));
    const currentBlockConfig = blockTypeToBlockName[blockType];
    const handleItemClick = useCallback((item) => {
        const formatParagraph = () => {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $setBlocksType(selection, () => $createParagraphNode());
                }
            });
        };
        switch (item.id) {
            case 'paragraph':
                formatParagraph();
                break;
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6': {
                // Corresponds to formatHeading() in Playground
                const headingLevel = item.id;
                if (blockType !== headingLevel) {
                    editor.update(() => {
                        const selection = $getSelection();
                        $setBlocksType(selection, () => $createHeadingNode(headingLevel));
                    });
                }
                break;
            }
            case 'bullet':
                if (blockType !== 'bullet') {
                    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                }
                else {
                    formatParagraph();
                }
                break;
            case 'check':
                if (blockType !== 'check') {
                    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
                }
                else {
                    formatParagraph();
                }
                break;
            case 'number':
                if (blockType !== 'number') {
                    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                }
                else {
                    formatParagraph();
                }
                break;
            case 'code':
                if (blockType !== 'code') {
                    editor.update(() => {
                        let selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            if (selection.isCollapsed()) {
                                $setBlocksType(selection, () => $createCodeNode());
                            }
                            else {
                                const textContent = selection.getTextContent();
                                const codeNode = $createCodeNode();
                                selection.insertNodes([codeNode]);
                                selection = $getSelection();
                                if ($isRangeSelection(selection)) {
                                    selection.insertRawText(textContent);
                                }
                            }
                        }
                    });
                }
                break;
            default:
                assertExhaustive(item.id);
        }
    }, [blockType, editor]);
    return (_jsx(ButtonDropdown, { disabled: disabled, items: items, iconLeft: currentBlockConfig.icon, renderItem: (item) => (_jsxs(_Fragment, { children: [_jsx(Icon, { className: toSpacingClassName({ marginRight: 2, paddingTop: 2 }), icon: item.icon, size: "small" }), item.name] })), onItemClick: handleItemClick, children: currentBlockConfig.title }));
}
function CodeLanguageDropdown({ disabled, codeLanguage, editor, selectedElementKey, }) {
    const items = Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP).map(([value, name]) => ({
        id: value,
        name,
    }));
    const handleItemClick = useCallback((item) => {
        editor.update(() => {
            if (selectedElementKey !== null) {
                const node = $getNodeByKey(selectedElementKey);
                if ($isCodeNode(node)) {
                    node.setLanguage(item.id);
                }
            }
        });
    }, [editor, selectedElementKey]);
    return (_jsx(ButtonDropdown, { disabled: disabled, items: items, renderItem: (it) => it.name, activeItemIds: [codeLanguage], onItemClick: handleItemClick, children: getLanguageFriendlyName(codeLanguage) }));
}
function AddEntityDialog({ fieldSpec, onClose, }) {
    const [editor] = useLexicalComposerContext();
    return (_jsx(Dialog2.Trigger, { defaultOpen: true, onOpenChange: onClose, children: _jsx(AdminEntitySelectorDialog, { title: "Select entity", entityTypes: fieldSpec.entityTypes, onItemClick: (entity) => {
                editor.dispatchCommand(INSERT_ADMIN_ENTITY_COMMAND, { id: entity.id });
                onClose();
            } }) }));
}
function AddComponentButton({ fieldSpec, onClose, }) {
    const [editor] = useLexicalComposerContext();
    return (_jsx(Dialog2.Trigger, { defaultOpen: true, onOpenChange: onClose, children: _jsx(AdminTypePickerDialog, { title: "Select component type", showComponentTypes: true, componentTypes: fieldSpec.componentTypes, onItemClick: (type) => {
                editor.dispatchCommand(INSERT_ADMIN_COMPONENT_COMMAND, { type });
                onClose();
            } }) }));
}
function LinkAndEntityLinkButton({ fieldSpec, enableLinkNode, enableEntityLinkNode, isLink, isEntityLink, }) {
    const [editor] = useLexicalComposerContext();
    const [openDialog, setOpenDialog] = useState(null);
    const handleOpenChanged = useCallback((isOpen) => {
        if (!isOpen) {
            setOpenDialog(null);
        }
    }, []);
    const handleToggleButton = useCallback(() => {
        if (isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        }
        else if (isEntityLink) {
            editor.dispatchCommand(TOGGLE_ADMIN_ENTITY_LINK_COMMAND, null);
        }
        else if (enableLinkNode && enableEntityLinkNode) {
            setOpenDialog('link');
        }
        else if (enableLinkNode) {
            showLinkPrompt(editor);
        }
        else if (enableEntityLinkNode) {
            setOpenDialog('entity');
        }
    }, [editor, enableEntityLinkNode, enableLinkNode, isEntityLink, isLink]);
    return (_jsxs(Dialog2.Trigger, { isOpen: openDialog !== null, onOpenChange: handleOpenChanged, children: [_jsx(IconButton, { icon: "link", toggled: isLink || isEntityLink, onClick: handleToggleButton }), openDialog === 'entity' && (_jsx(AdminEntitySelectorDialog, { title: "Select entity", entityTypes: fieldSpec.linkEntityTypes, onItemClick: (entity) => {
                    editor.dispatchCommand(TOGGLE_ADMIN_ENTITY_LINK_COMMAND, { id: entity.id });
                    setOpenDialog(null);
                } })), openDialog === 'link' && (_jsx(CreateLinkDialog, { onCreateLink: () => {
                    setOpenDialog(null);
                    showLinkPrompt(editor);
                }, onCreateEntityLink: () => {
                    setOpenDialog('entity');
                } }))] }));
}
function showLinkPrompt(editor) {
    const url = window.prompt('Enter the URL', 'https://');
    const sanitizedUrl = sanitizeUrl(url);
    if (sanitizedUrl) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizedUrl);
    }
}
function sanitizeUrl(url) {
    if (!url)
        return null;
    try {
        const urlObj = new URL(url);
        if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
            return urlObj.toString();
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }
    catch (e) {
        // ignore
    }
    return null;
}
//# sourceMappingURL=ToolbarPlugin.js.map